import { HttpError, httpErrorCode } from '@/services/http';

/**
 * Plan limit reached on a plan someone else pays (`viewerIsPayer: false`): the
 * user cannot upgrade it, the payer must. Absent on an older backend: plans offered.
 */
export const planLimitAsksPayer = (error: unknown): boolean =>
  httpErrorCode(error) === 'PLAN_LIMIT_REACHED' &&
  error instanceof HttpError &&
  typeof error.body === 'object' &&
  error.body !== null &&
  'viewerIsPayer' in error.body &&
  error.body.viewerIsPayer === false;

/**
 * Locked team refused to someone who cannot reactivate it (`viewerCanReactivate:
 * false`, VOL-199): told to see the subscription holder instead of "reactivate
 * it". Absent on an older backend: the usual text.
 */
export const teamLockedAsksPayer = (error: unknown): boolean =>
  httpErrorCode(error) === 'TEAM_LOCKED' &&
  error instanceof HttpError &&
  typeof error.body === 'object' &&
  error.body !== null &&
  'viewerCanReactivate' in error.body &&
  error.body.viewerCanReactivate === false;

/**
 * When a locked team can be reactivated by this user, if the 7-day delay after
 * an active team swap still holds it (`reactivableAt`, VOL-199). Null otherwise,
 * or once the date has passed.
 */
export function teamLockedReactivableAt(error: unknown, now: Date = new Date()): Date | null {
  if (httpErrorCode(error) !== 'TEAM_LOCKED' || !(error instanceof HttpError)) return null;
  const body = error.body;
  if (typeof body !== 'object' || body === null || !('reactivableAt' in body)) return null;
  if (typeof body.reactivableAt !== 'string') return null;
  const at = new Date(body.reactivableAt);
  return Number.isNaN(at.getTime()) || at <= now ? null : at;
}

/**
 * Translated message of a plan limit refusal (the API message is French), or
 * undefined for any other error.
 */
export function planLimitMessageKey<K extends string>(
  error: unknown,
  keys: { upgrade: K; askPayer: K },
): K | undefined {
  if (httpErrorCode(error) !== 'PLAN_LIMIT_REACHED') return undefined;
  return planLimitAsksPayer(error) ? keys.askPayer : keys.upgrade;
}
